import { useFormik } from 'formik';
import { useLottie } from 'lottie-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { BrandedSignUpForm, ClassicSignUpForm } from './SignUpForms';
import { runThirdPartySignUp, SignUpFormValues, submitSignUp } from './signUpHelpers';
import { createSignupSchema } from './signUpSchema';
import * as successAnimation from '../../assets/success-animation.json';
import { useAuthValue } from '../../context/AuthContext';
import { isBrandedPlatform } from '../../utils';

export interface SignUpComponentProps {
    email: string;
    organizationName: string;
    inviteUuid: string;
    userExternalId?: string;
}

// After signup process completes, App component will be checking wether the observable user in context changes
// If user's auth state changes and has a verified email address, App will redirect him to the login page
// Note: It does this through firebase onAuthStateChanged method. See: https://firebase.google.com/docs/auth/web/manage-users
export function SignUpComponent(props: SignUpComponentProps) {
    const [loading, setLoading] = useState(false);
    const [signupSuccess, setSignupSuccess] = useState(false);
    const context = useAuthValue();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isActivation = searchParams.get('type') === 'activation';
    const externalId = props.userExternalId || '';

    const formik = useFormik<SignUpFormValues>({
        initialValues: {
            firstName: '',
            lastName: '',
            email: props.email,
            phone: '',
            password: '',
        },
        validateOnMount: true,
        validationSchema: createSignupSchema(isActivation),
        onSubmit: (values, { setSubmitting }) =>
            submitSignUp({
                values,
                inviteUuid: props.inviteUuid,
                userExternalId: props.userExternalId,
                context,
                navigate,
                setSignupSuccess,
                setSubmitting,
            }),
    });

    const handleGoogleSignUp = () =>
        runThirdPartySignUp(
            () => context?.signUpWithGoogle(props.inviteUuid, externalId) ?? Promise.resolve(),
            navigate,
            setLoading,
        );

    const handleMicrosoftSignUp = () =>
        runThirdPartySignUp(
            () => context?.signUpWithMicrosoft(props.inviteUuid, externalId) ?? Promise.resolve(),
            navigate,
            setLoading,
        );

    const handleAppleSignUp = () =>
        runThirdPartySignUp(
            () => context?.signUpWithApple(props.inviteUuid, externalId) ?? Promise.resolve(),
            navigate,
            setLoading,
        );

    const lottieOptions = {
        loop: false,
        autoplay: true,
        animationData: successAnimation,
    };

    const { View: SuccessAnimation } = useLottie(lottieOptions);

    if (isBrandedPlatform) {
        return (
            <BrandedSignUpForm
                formik={formik}
                email={props.email}
                isActivation={isActivation}
                loading={loading}
                signupSuccess={signupSuccess}
                successAnimation={SuccessAnimation}
                organizationName={props.organizationName}
                onGoogleSignUp={handleGoogleSignUp}
                onMicrosoftSignUp={handleMicrosoftSignUp}
                onAppleSignUp={handleAppleSignUp}
            />
        );
    }

    return (
        <ClassicSignUpForm
            formik={formik}
            loading={loading}
            signupSuccess={signupSuccess}
            successAnimation={SuccessAnimation}
            organizationName={props.organizationName}
            onGoogleSignUp={handleGoogleSignUp}
        />
    );
}

export default SignUpComponent;
